import { auth, googleProvider, appleProvider, signInWithPopup, fetchSignInMethodsForEmail, linkWithCredential, linkWithPopup, signInWithCredential, updateProfile, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification, GoogleAuthProvider, OAuthProvider, EmailAuthProvider, getAdditionalUserInfo } from '../firebase-config.js';

export class AuthController {
    constructor() {
        this.currentUser = null;
        this.authStateListeners = [];
        // Social sign-in state machine (Task #19)
        //   _inFlightProvider: 'google' | 'apple' | null — provider whose popup is currently open
        //   _pendingProvider:  'google' | 'apple' | null — queued provider awaiting Firebase cooldown
        //   _pendingRetryTimer: id of the scheduled auto-retry, so we can cancel on modal close
        this._inFlightProvider = null;
        this._pendingProvider = null;
        this._pendingRetryTimer = null;
        this.isFirebaseAvailable = this.checkFirebaseAvailability();
        
        // Only initialize Firebase features if available
        if (this.isFirebaseAvailable) {
            this.initializeAuth();
            this.setupEventListeners();
            this.setupVerificationListener();
        } else {
            console.info('AuthController: Firebase not available - authentication features disabled');
        }
    }
    
    setupVerificationListener() {
        // Listen for global verification-required events from other auth handlers
        window.addEventListener('firebase-auth-verification-required', async (event) => {
            console.log('[AuthController] Received verification-required event:', event.detail);
            
            // If user just signed up, ignore this event - we want to keep them signed in
            // so they can see "Free Questions" and click to verify
            // Check both window flag and sessionStorage (for page reloads)
            const justSignedUpFlag = window.justSignedUp || sessionStorage.getItem('justSignedUp') === 'true';
            if (justSignedUpFlag) {
                console.log('[AuthController] Ignoring verification event - user just signed up, keeping them signed in');
                return;
            }
            
            const email = event.detail?.email;
            if (email) {
                // Ensure user is signed out (other handlers should have done this, but be safe)
                try {
                    if (auth && auth.currentUser) {
                        await signOut(auth);
                        console.log('[AuthController] Signed out unverified user from event listener');
                    }
                } catch (signOutError) {
                    console.error('[AuthController] Error signing out in verification listener:', signOutError);
                }
                
                // Show the verification required message
                this.showVerificationRequiredMessage(email);
            }
        });
    }
    
    checkFirebaseAvailability() {
        // Check if Firebase modules are available and not null
        return !!(auth && onAuthStateChanged && signInWithPopup && googleProvider);
    }
    
    showFirebaseUnavailableMessage() {
        // Show a user-friendly message when Firebase is not available
        const message = 'Authentication is currently unavailable. You can still use the app without signing in.';
        if (window.alert) {
            window.alert(message);
        }
        console.info(message);
    }

    // -----------------------------------------------------------------
    // Social sign-in UI state helpers (Task #19)
    // -----------------------------------------------------------------
    _socialButton(provider) {
        if (provider === 'google') return document.getElementById('google-signin-btn');
        if (provider === 'apple')  return document.getElementById('main-apple-signin-btn');
        if (provider === 'email')  return document.getElementById('email-signin-btn');
        return null;
    }

    _setButtonState(provider, state /* 'idle' | 'connecting' | 'waiting' | 'disabled' */) {
        const btn = this._socialButton(provider);
        if (!btn) return;
        // Stash original markup once so we can restore it cleanly
        if (!btn.dataset.originalHtml) {
            btn.dataset.originalHtml = btn.innerHTML;
        }
        btn.classList.remove('auth-btn--connecting', 'auth-btn--waiting', 'auth-btn--locked');
        if (state === 'idle') {
            btn.disabled = false;
            btn.innerHTML = btn.dataset.originalHtml;
            return;
        }
        btn.disabled = true;
        if (state === 'disabled') {
            btn.classList.add('auth-btn--locked');
            btn.innerHTML = btn.dataset.originalHtml;
            return;
        }
        const labelMap = {
            connecting: { google: 'Connecting to Google…', apple: 'Connecting to Apple…' },
            waiting:    { google: 'Just a moment…',         apple: 'Just a moment…' }
        };
        const label = labelMap[state]?.[provider] || 'Working…';
        btn.classList.add(state === 'connecting' ? 'auth-btn--connecting' : 'auth-btn--waiting');
        btn.innerHTML = `<span class="auth-btn-spinner" aria-hidden="true"></span><span>${label}</span>`;
    }

    _beginSocial(provider) {
        this._inFlightProvider = provider;
        this._setButtonState('google', provider === 'google' ? 'connecting' : 'disabled');
        this._setButtonState('apple',  provider === 'apple'  ? 'connecting' : 'disabled');
        this._setButtonState('email',  'disabled');
    }

    _endSocial() {
        this._inFlightProvider = null;
        if (this._pendingProvider) {
            const pending = this._pendingProvider;
            this._pendingProvider = null;
            // Reset everything, then put just the pending button into waiting state
            this._setButtonState('google', 'idle');
            this._setButtonState('apple',  'idle');
            this._setButtonState('email',  'idle');
            this._setButtonState(pending, 'waiting');
            if (this._pendingRetryTimer) clearTimeout(this._pendingRetryTimer);
            // Wait past Firebase's internal popup cooldown (~1–3s observed) and fire automatically
            this._pendingRetryTimer = setTimeout(() => {
                this._pendingRetryTimer = null;
                if (pending === 'google') {
                    this.signInWithGoogle({ _isAutoRetry: true });
                } else if (pending === 'apple') {
                    this.signInWithApple({ _isAutoRetry: true });
                }
            }, 1500);
        } else {
            this._setButtonState('google', 'idle');
            this._setButtonState('apple',  'idle');
            this._setButtonState('email',  'idle');
        }
    }

    _queuePending(provider) {
        this._pendingProvider = provider;
        // Show waiting state on the queued button (unless it's the one currently connecting,
        // in which case the connecting state stays — _endSocial will swap it to waiting)
        if (this._inFlightProvider !== provider) {
            this._setButtonState(provider, 'waiting');
        }
    }

    _cancelPendingSocial() {
        if (this._pendingRetryTimer) {
            clearTimeout(this._pendingRetryTimer);
            this._pendingRetryTimer = null;
        }
        this._pendingProvider = null;
        this._inFlightProvider = null;
        this._setButtonState('google', 'idle');
        this._setButtonState('apple',  'idle');
        this._setButtonState('email',  'idle');
    }

    initializeAuth() {
        // Note: signInWithRedirect doesn't work in Replit's iframe environment
        // We use popup authentication only
        
        // Ensure Firebase is available before using it
        if (!this.isFirebaseAvailable) {
            return;
        }
        
        // Listen for authentication state changes
        onAuthStateChanged(auth, (user) => {
            // Note: emailSignUpInProgress flag is no longer used to skip processing
            // since we now keep users signed in after signup
            
            this.currentUser = user;
            this.updateUI();
            
            // Notify listeners
            this.authStateListeners.forEach(listener => {
                listener(user);
            });
            
            if (user) {
                console.log('User signed in:', user.displayName || user.email);
                
                // Pre-initialize question counter immediately on sign-in
                if (window.chatController && window.chatController.questionCounter) {
                    console.log('[AuthController] Pre-initializing question counter for signed-in user');
                    window.chatController.questionCounter.initializeForUser(user.uid).then(() => {
                        console.log('[AuthController] Question counter pre-initialized successfully');
                    }).catch(error => {
                        console.error('[AuthController] Failed to pre-initialize question counter:', error);
                    });
                }
                
                // Process pending message if user just signed in and there's a message waiting
                if (window.chatController && window.chatController.pendingMessage) {
                    console.log('Processing pending message after successful sign-in');
                    const pendingMessage = window.chatController.pendingMessage;
                    window.chatController.pendingMessage = null; // Clear pending message
                    
                    // Close auth modal
                    this.closeAuthModal();
                    
                    // Process the pending message
                    setTimeout(() => {
                        window.chatController.sendMessage(pendingMessage, true);
                    }, 300); // Small delay to ensure modal is closed
                }
            } else {
                console.log('User signed out');
            }
        });
    }

    // Redirect authentication is not supported in Replit's iframe environment
    // This method is kept for compatibility but is not used
    async handleRedirectResult() {
        // Not used - Replit runs in an iframe which blocks redirects
        console.log('Redirect authentication is not supported in this environment');
    }

    setupEventListeners() {
        console.log('🔧 AuthController: Setting up event listeners');
        
        // Social Sign In
        document.addEventListener('click', (e) => {
            console.log('🔧 Click detected on:', e.target);
            console.log('🔧 Target element:', e.target.tagName, e.target.id, e.target.className);
            
            const googleBtn = e.target.closest('button#google-signin-btn');
            const appleBtn = e.target.closest('button#apple-signin-btn, button#main-apple-signin-btn');
            const emailSigninBtn = e.target.closest('button#email-signin-btn, a#email-signin-btn');
            const emailSignupBtn = e.target.closest('button#email-signup-btn, a#email-signup-btn');
            const signoutBtn = e.target.closest('button#signout-btn');
            const closeBtn = e.target.closest('button#close-auth-modal, .btn-close');
            const forgotBtn = e.target.closest('a#forgot-password-btn');
            const backBtn = e.target.closest('a#back-to-signin-btn');
            
            console.log('🔧 Button detection results:');
            console.log('🔧 googleBtn:', googleBtn);
            console.log('🔧 appleBtn:', appleBtn);
            console.log('🔧 emailSigninBtn:', emailSigninBtn);
            
            if (googleBtn) {
                console.log('🔧 Google button clicked - calling signInWithGoogle()');
                this.signInWithGoogle();
            }
            if (appleBtn) {
                console.log('🍎 Apple button clicked - calling signInWithApple()');
                this.signInWithApple();
            }
            if (emailSigninBtn) {
                console.log('🔧 Email signin button clicked');
                this.showEmailSignInForm();
            }
            if (emailSignupBtn) {
                console.log('🔧 Email signup button clicked');
                this.showEmailSignUpForm();
            }
            if (signoutBtn) {
                console.log('🔧 Signout button clicked');
                this.signOut();
            }
            if (closeBtn) {
                console.log('🔧 Close button clicked');
                this.closeAuthModal();
            }
            if (forgotBtn) {
                console.log('🔧 Forgot password button clicked');
                this.showForgotPasswordForm();
            }
            if (backBtn) {
                console.log('🔧 Back button clicked');
                this.showEmailSignInForm();
            }
        });

        // Form submissions
        document.addEventListener('submit', (e) => {
            if (e.target.id === 'email-signin-form') {
                e.preventDefault();
                this.signInWithEmail();
            }
            if (e.target.id === 'email-signup-form') {
                e.preventDefault();
                this.signUpWithEmail();
            }
            if (e.target.id === 'forgot-password-form') {
                e.preventDefault();
                this.sendPasswordReset();
            }
        });
    }

    // Route a social-sign-in error to the visible login window. Prefers the new
    // Tuscan-dusk modal's inline error line (#modal-auth .auth-error-line); when
    // that modal isn't open it falls back to the legacy hidden modal. `plain` is
    // a short text message for the new modal; `html` is the richer legacy markup.
    _routeAuthError(plain, html) {
        if (typeof window.showNewUiAuthError === 'function' && window.showNewUiAuthError(plain)) {
            return;
        }
        if (window.showAuthError) {
            window.showAuthError(html || plain);
            if (window.showSigninForm) window.showSigninForm();
        }
    }

    // Sign in with a social provider, LINKING it onto the current anonymous user
    // when one exists so the anon uid (and its free-taste state) is preserved.
    // If that credential is already attached to a different real account, fall
    // back to signing into that existing account. Emits `account_linked`.
    async _signInOrLinkProvider(provider) {
        const cu = auth && auth.currentUser;
        if (cu && cu.isAnonymous && typeof linkWithPopup === 'function') {
            try {
                const res = await linkWithPopup(cu, provider);
                // Anonymous free-taste upgraded to a REAL account — a new signup
                // (getAdditionalUserInfo(...).isNewUser is false for links, so the
                // provider flows below won't catch this). Deduped per-uid.
                try { window.llFireLead && window.llFireLead(res.user?.uid, 'provider_link'); } catch (e) {}
                try {
                    window.trackEvent && window.trackEvent('account_linked', {
                        provider: provider?.providerId || 'unknown',
                        method: 'link',
                    });
                } catch (e) {}
                return res;
            } catch (err) {
                if (err && err.code === 'auth/credential-already-in-use') {
                    // The chosen identity already belongs to a real account.
                    // We can't merge anon data into it client-side, so sign into
                    // the existing account instead (anon session is discarded).
                    const cred =
                        OAuthProvider.credentialFromError(err) ||
                        GoogleAuthProvider.credentialFromError(err);
                    if (cred && typeof signInWithCredential === 'function') {
                        const res = await signInWithCredential(auth, cred);
                        try {
                            window.trackEvent && window.trackEvent('account_linked', {
                                provider: provider?.providerId || 'unknown',
                                method: 'existing_account',
                            });
                        } catch (e) {}
                        return res;
                    }
                }
                throw err;
            }
        }
        return await signInWithPopup(auth, provider);
    }

    async signInWithGoogle({ _isAutoRetry = false } = {}) {
        // Check if Firebase is available
        if (!this.isFirebaseAvailable) {
            console.warn('AuthController: Cannot sign in - Firebase not available');
            this.showFirebaseUnavailableMessage();
            return;
        }

        // If another social attempt is already in flight, queue this one so it fires
        // automatically when the in-flight attempt finishes (Task #19).
        if (this._inFlightProvider) {
            console.log(`[Auth] Queueing Google sign-in (currently in flight: ${this._inFlightProvider})`);
            this._queuePending('google');
            return;
        }

        try {
            this._beginSocial('google');
            const result = await this._signInOrLinkProvider(googleProvider);
            const user = result.user;
            console.log('Google sign-in successful:', user);

            // X (Twitter) Lead — NEW accounts only, decided by the SDK's
            // authoritative isNewUser flag (a missing profile doc is NOT proof of
            // a new account, and returning sign-ins must never fire Lead).
            try {
                if (getAdditionalUserInfo(result)?.isNewUser === true) {
                    window.llFireLead && window.llFireLead(user.uid, 'google');
                }
            } catch (e) {}
            
            // Check if user profile exists and create if needed
            if (window.getUserProfile && window.saveUserProfile) {
                const existingProfile = await window.getUserProfile(user.uid);
                if (!existingProfile) {
                    // Create profile for new Google users
                    // Google users are auto-verified, so grant 10 free questions immediately
                    const displayName = user.displayName || user.email.split('@')[0];
                    const username = displayName.replace(/\s+/g, '_').toLowerCase();
                    await window.saveUserProfile(user.uid, username, user.email, 10, true);
                    console.log('Created user profile for Google user:', username, '(verified, 10 free questions)');
                }
            }

            // Google is provider-verified — start the welcome series (idempotent; recent signups only).
            this.triggerWelcomeSequence(user);
            
            // Use the instance method for proper cleanup with a small delay
            // to ensure all auth state is properly updated
            setTimeout(() => {
                this.closeAuthModal();
                // Force a UI update after closing modal
                this.updateUI();
            }, 100);
        } catch (error) {
            console.error('Google sign-in error:', error);
            
            if (error.code === 'auth/popup-blocked') {
                // Popups are blocked - show instructions to user
                // Note: signInWithRedirect doesn't work in Replit's iframe environment
                console.log('Popup blocked - showing instructions to user');
                this._routeAuthError(
                    'Pop-up blocked. Allow pop-ups for this site (look for the icon in your address bar), then try again.',
                    `
                    <strong>⚠️ Popup Blocked</strong><br><br>
                    To sign in with Google, please:<br>
                    1. Look for the popup blocker icon in your browser's address bar 🔒<br>
                    2. Click it and select "Always allow popups from this site"<br>
                    3. Try signing in again<br><br>
                    <small>Note: Popups are required for Google sign-in in this environment.</small>
                `);
            } else if (error.code === 'auth/cancelled-popup-request') {
                // Firebase's internal popup cooldown rejected our call. Auto-retry once;
                // if the queued retry also fails, surface an inline hint.
                console.log('[Auth] Google sign-in hit popup cooldown — auto-retrying');
                if (!_isAutoRetry) {
                    this._queuePending('google');
                } else {
                    this._routeAuthError('One moment — finishing up the previous sign-in. Please try again.');
                }
            } else if (error.code === 'auth/popup-closed-by-user') {
                // User cancelled the sign-in, don't show error
                console.log('Google sign-in cancelled by user');
            } else if (error.code === 'auth/account-exists-with-different-credential') {
                await this.handleAccountExistsError(error);
            } else if (error.code === 'auth/unauthorized-domain') {
                this._routeAuthError('Domain not authorized. Please use email sign-in or contact support to enable Google sign-in for this domain.');
            } else if (error.code === 'auth/operation-not-supported-in-this-environment') {
                this._routeAuthError('Google sign-in is not supported in this environment. Please try email sign-in instead.');
            } else {
                this._routeAuthError(`Authentication error: ${error.code}. Please try again or contact support.`);
            }
        } finally {
            // Always release UI lock; auto-fires any pending provider after cooldown
            this._endSocial();
        }
    }

    async signInWithApple({ _isAutoRetry = false } = {}) {
        console.log('🍎 signInWithApple() called - starting Apple authentication');

        // If another social attempt is already in flight, queue this one so it fires
        // automatically when the in-flight attempt finishes (Task #19).
        if (this._inFlightProvider) {
            console.log(`[Auth] Queueing Apple sign-in (currently in flight: ${this._inFlightProvider})`);
            this._queuePending('apple');
            return;
        }

        try {
            this._beginSocial('apple');
            console.log('🍎 Creating Apple OAuth provider');
            // Following Firebase v9+ documentation format
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            console.log('🍎 Provider configured, calling signInWithPopup');
            
            const result = await this._signInOrLinkProvider(provider);
            console.log('🍎 Apple sign-in successful!');
            console.log(result.user); // logged-in Apple user
            
            const user = result.user;

            // X (Twitter) Lead — NEW accounts only. NOTE: the legacy
            // result.additionalUserInfo path below never resolves under the
            // modular SDK, so Apple Leads historically NEVER fired; the modular
            // getAdditionalUserInfo(result) helper is the working signal.
            try {
                if (getAdditionalUserInfo(result)?.isNewUser === true) {
                    window.llFireLead && window.llFireLead(user.uid, 'apple');
                }
            } catch (e) {}

            // Check if user profile exists and create if needed
            if (window.getUserProfile && window.saveUserProfile) {
                const existingProfile = await window.getUserProfile(user.uid);
                if (!existingProfile) {
                    // For Apple users, try to get display name from user object or use email
                    // Apple users are auto-verified, so grant 10 free questions immediately
                    const displayName = user.displayName || user.email?.split('@')[0] || 'AppleUser';
                    const username = displayName.replace(/\s+/g, '_').toLowerCase();
                    await window.saveUserProfile(user.uid, username, user.email, 10, true);
                    console.log('Created user profile for Apple user:', username, '(verified, 10 free questions)');
                }
            }

            // Apple is provider-verified — start the welcome series (idempotent; recent signups only).
            this.triggerWelcomeSequence(user);
            
            // Use the instance method for proper cleanup with a small delay
            // to ensure all auth state is properly updated
            setTimeout(() => {
                this.closeAuthModal();
                // Force a UI update after closing modal
                this.updateUI();
            }, 100);
            
            // Handle Apple name data on first sign-in
            if (result.additionalUserInfo?.isNewUser) {
                await this.handleAppleUserData(user, result);
            }
        } catch (error) {
            console.error('🍎 Apple sign-in error:', error);
            
            if (error.code === 'auth/popup-blocked') {
                // Popups are blocked - show instructions to user
                // Note: signInWithRedirect doesn't work in Replit's iframe environment
                console.log('Popup blocked - showing instructions to user');
                this._routeAuthError(
                    'Pop-up blocked. Allow pop-ups for this site (look for the icon in your address bar), then try again.',
                    `
                        <strong>⚠️ Popup Blocked</strong><br><br>
                        To sign in with Apple, please:<br>
                        1. Look for the popup blocker icon in your browser's address bar 🔒<br>
                        2. Click it and select "Always allow popups from this site"<br>
                        3. Try signing in again<br><br>
                        <small>Note: Popups are required for Apple sign-in in this environment.</small>
                    `);
            } else if (error.code === 'auth/cancelled-popup-request') {
                // Firebase's internal popup cooldown rejected our call. Auto-retry once;
                // if the queued retry also fails, surface an inline hint.
                console.log('[Auth] Apple sign-in hit popup cooldown — auto-retrying');
                if (!_isAutoRetry) {
                    this._queuePending('apple');
                } else {
                    this._routeAuthError('One moment — finishing up the previous sign-in. Please try again.');
                }
            } else if (error.code === 'auth/account-exists-with-different-credential') {
                await this.handleAccountExistsError(error);
            } else if (error.code === 'auth/unauthorized-domain') {
                this._routeAuthError('Domain not authorized. Please use email sign-in or contact support to enable Apple sign-in for this domain.');
            } else if (error.code === 'auth/popup-closed-by-user') {
                // User cancelled the sign-in, don't show error
                console.log('Apple sign-in cancelled by user');
            } else if (error.code === 'auth/operation-not-supported-in-this-environment') {
                this._routeAuthError('Apple sign-in is not supported in this environment. Please try email sign-in instead.');
            } else {
                this._routeAuthError('Failed to sign in with Apple. Please try again or use email sign-in.');
            }
        } finally {
            // Always release UI lock; auto-fires any pending provider after cooldown
            this._endSocial();
        }
    }

    isIOSOrSafari() {
        const userAgent = navigator.userAgent;
        return /iPad|iPhone|iPod/.test(userAgent) || 
               (/Safari/.test(userAgent) && !/Chrome|Chromium|Edge/.test(userAgent));
    }

    async handleAppleUserData(user, result) {
        try {
            // Apple provides name data only on first authorization
            const appleData = result.additionalUserInfo?.profile;
            if (appleData?.name && !user.displayName) {
                const displayName = `${appleData.name.firstName || ''} ${appleData.name.lastName || ''}`.trim();
                if (displayName) {
                    await updateProfile(user, { displayName });
                    console.log('Updated user profile with Apple name:', displayName);
                }
            }
        } catch (error) {
            console.error('Error updating Apple user profile:', error);
            // Don't show error to user as this is non-critical
        }
    }

    async handleAccountExistsError(error) {
        try {
            const email = error.customData?.email;
            const pendingCredential = OAuthProvider.credentialFromError(error) || GoogleAuthProvider.credentialFromError(error);
            
            if (!email) {
                this.showErrorMessage('Account linking failed. Please try signing in with your existing method.');
                return;
            }

            // Get existing sign-in methods for this email
            const signInMethods = await fetchSignInMethodsForEmail(auth, email);
            
            if (signInMethods.length === 0) {
                this.showErrorMessage('No existing account found. Please try creating a new account.');
                return;
            }

            // Show account linking modal
            this.showAccountLinkingModal(email, signInMethods, pendingCredential);
            
        } catch (fetchError) {
            console.error('Error handling account exists:', fetchError);
            this.showErrorMessage('Account linking failed. Please try signing in with your existing method.');
        }
    }

    showAccountLinkingModal(email, signInMethods, pendingCredential) {
        const authContent = document.getElementById('auth-modal-content');
        const providerNames = {
            'google.com': 'Google',
            'password': 'Email/Password',
            'apple.com': 'Apple'
        };
        
        const existingMethods = signInMethods.map(method => providerNames[method] || method).join(', ');
        
        authContent.innerHTML = `
            <div class="auth-form text-center">
                <div class="mb-4">
                    <i class="fas fa-link" style="font-size: 3rem; color: #ffc107;"></i>
                </div>
                <h3 class="mb-4">Account Already Exists</h3>
                <p class="mb-4" style="color: rgba(255, 255, 255, 0.8);">
                    An account with the email <strong>${email}</strong> already exists.
                </p>
                <p class="mb-4" style="color: rgba(255, 255, 255, 0.8);">
                    Please sign in with your existing method (${existingMethods}) first, then you can link your Apple account.
                </p>
                <div class="d-grid gap-2">
                    ${signInMethods.includes('google.com') ? 
                        `<button class="btn btn-outline-secondary" id="link-google-signin">
                            <i class="fab fa-google me-2"></i>Sign in with Google to Link
                        </button>` : ''}
                    ${signInMethods.includes('password') ? 
                        `<button class="btn btn-outline-secondary" id="link-email-signin">
                            <i class="fas fa-envelope me-2"></i>Sign in with Email to Link
                        </button>` : ''}
                    <button class="btn btn-secondary" id="cancel-linking">
                        Cancel
                    </button>
                </div>
            </div>
        `;
        
        // Store pending credential for later linking
        this.pendingCredential = pendingCredential;
        this.linkingEmail = email;
        
        // Add event listeners for linking buttons
        document.getElementById('link-google-signin')?.addEventListener('click', () => this.linkWithGoogle());
        document.getElementById('link-email-signin')?.addEventListener('click', () => this.showEmailSignInForLinking());
        document.getElementById('cancel-linking')?.addEventListener('click', () => this.cancelLinking());
    }

    async linkWithGoogle() {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            await this.completeLinking(result.user);
        } catch (error) {
            console.error('Google sign-in for linking failed:', error);
            
            if (error.code === 'auth/popup-blocked') {
                // Popups are blocked - show instructions to user
                console.log('Popup blocked - showing instructions to user');
                this.showErrorMessage(`
                    <strong>⚠️ Popup Blocked</strong><br><br>
                    To link your Google account, please:<br>
                    1. Look for the popup blocker icon in your browser's address bar 🔒<br>
                    2. Click it and select "Always allow popups from this site"<br>
                    3. Try linking again<br><br>
                    <small>Note: Popups are required for account linking in this environment.</small>
                `);
            } else if (error.code === 'auth/cancelled-popup-request') {
                console.log('Duplicate popup request cancelled');
                this.showErrorMessage('Please wait for the sign-in window to open.');
            } else if (error.code === 'auth/popup-closed-by-user') {
                // User cancelled the sign-in, don't show error
                console.log('Google linking cancelled by user');
            } else {
                this.showErrorMessage('Failed to sign in with Google. Please try again.');
            }
        }
    }

    showEmailSignInForLinking() {
        const authContent = document.getElementById('auth-modal-content');
        authContent.innerHTML = `
            <div class="auth-form">
                <h3 class="text-center mb-4">Sign In to Link Accounts</h3>
                <p class="text-center mb-4" style="color: rgba(255, 255, 255, 0.8);">
                    Sign in with your existing email/password to link your Apple account.
                </p>
                <form id="linking-signin-form">
                    <div class="mb-3">
                        <label for="linking-email" class="form-label">Email</label>
                        <input type="email" class="form-control" id="linking-email" value="${this.linkingEmail}" required readonly>
                    </div>
                    <div class="mb-3">
                        <label for="linking-password" class="form-label">Password</label>
                        <input type="password" class="form-control" id="linking-password" required>
                    </div>
                    <button type="submit" class="btn btn-primary w-100 mb-3">Sign In & Link Account</button>
                </form>
                <div class="text-center">
                    <button class="btn btn-outline-secondary" id="cancel-linking">Cancel</button>
                </div>
            </div>
        `;
        
        document.getElementById('linking-signin-form').addEventListener('submit', (e) => {
            e.preventDefault();
            this.signInForLinking();
        });
        
        document.getElementById('cancel-linking').addEventListener('click', () => this.cancelLinking());
    }

    async signInForLinking() {
        const email = document.getElementById('linking-email').value;
        const password = document.getElementById('linking-password').value;
        
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            await this.completeLinking(result.user);
        } catch (error) {
            console.error('Email sign-in for linking failed:', error);
            this.showErrorMessage(this.getErrorMessage(error.code));
        }
    }

    async completeLinking(user) {
        try {
            if (this.pendingCredential) {
                await linkWithCredential(user, this.pendingCredential);
                this.showSuccessMessage('Apple account successfully linked! You can now sign in with either method.');
            }
            this.closeAuthModal();
        } catch (error) {
            console.error('Account linking failed:', error);
            this.showErrorMessage('Failed to link accounts. Please try again.');
        } finally {
            this.pendingCredential = null;
            this.linkingEmail = null;
        }
    }

    cancelLinking() {
        this.pendingCredential = null;
        this.linkingEmail = null;
        this.closeAuthModal();
    }

    async signInWithEmail() {
        const email = document.getElementById('signin-email').value;
        const password = document.getElementById('signin-password').value;

        if (!email || !password) {
            this.showErrorMessage('Please fill in all fields.');
            return;
        }

        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            
            // Check if email is verified for email/password users
            if (!result.user.emailVerified) {
                console.log('[AuthController] User email not verified:', result.user.email);
                
                // Store user reference before showing modal (need it for resend)
                const unverifiedUser = result.user;
                
                // Show verification required UI with resend option
                // Note: We keep the user signed in temporarily so we can resend verification
                this.showVerificationRequiredModal(unverifiedUser);
                return;
            }
            
            this.closeAuthModal();
            this.showSuccessMessage(`Welcome back, ${result.user.email}!`);
            // Verified email/password user is now active — start the welcome series.
            this.triggerWelcomeSequence(result.user);
        } catch (error) {
            console.error('Email sign-in error:', error);
            this.showErrorMessage(this.getErrorMessage(error.code));
        }
    }
    
    // Ask the backend to generate a Firebase verification link and deliver a
    // branded email via SendGrid. Never throws — returns the parsed JSON (or a
    // failure object) so callers can react without try/catch noise.
    async requestVerificationEmail(user) {
        let idToken = null;
        const email = user?.email || null;
        try {
            if (user && typeof user.getIdToken === 'function') {
                idToken = await user.getIdToken();
            }
        } catch (e) {
            console.warn('[AuthController] Could not get id token for verification:', e);
        }
        if (typeof window.requestVerificationEmail === 'function') {
            return await window.requestVerificationEmail(email, idToken);
        }
        // Fallback if the inline helper isn't present.
        try {
            const res = await fetch('/api/auth/send-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, idToken })
            });
            return await res.json().catch(() => ({}));
        } catch (e) {
            console.warn('[AuthController] Verification request failed:', e);
            return { success: false };
        }
    }

    // Fire-and-forget: ask the backend to start the welcome/nurture email series
    // once this user is verified/active. The backend is idempotent (sends at most
    // once per user) and only targets recent signups, so it's safe to call on
    // every successful sign-in. Never throws.
    async triggerWelcomeSequence(user) {
        try {
            if (!user || typeof user.getIdToken !== 'function') return;
            const idToken = await user.getIdToken();
            // First-touch ad attribution rides along so the server can mirror
            // signup_completed + stamp attribution onto the profile.
            const attribution = (typeof window.llGetAttribution === 'function' && window.llGetAttribution()) || undefined;
            await fetch('/api/auth/welcome', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ idToken, attribution })
            });
        } catch (e) {
            console.warn('[AuthController] Welcome sequence trigger failed (non-fatal):', e);
        }
    }

    async showVerificationRequiredModal(user) {
        const authContent = document.getElementById('auth-modal-content');
        
        // Store user email before signing out (for display and resend)
        const userEmail = user.email;
        
        // Send a branded verification email via SendGrid (backend generates the
        // Firebase action link). Grab the result so we can seed the cooldown.
        const vResult = await this.requestVerificationEmail(user);
        console.log('[AuthController] Verification email request result:', vResult);
        
        // Sign out the user immediately - they must verify before accessing the app
        try {
            await signOut(auth);
            console.log('[AuthController] Signed out unverified user:', userEmail);
        } catch (signOutError) {
            console.error('[AuthController] Error signing out unverified user:', signOutError);
        }
        
        authContent.innerHTML = `
            <div class="auth-form text-center">
                <div class="mb-4">
                    <i class="fas fa-envelope" style="font-size: 3rem; color: #ffc107;"></i>
                </div>
                <h3 class="mb-4">Email Verification Required</h3>
                <p class="mb-4" style="color: rgba(255, 255, 255, 0.8);">
                    We just sent a verification email to<br>
                    <strong>${userEmail}</strong>
                </p>
                <p class="mb-4" style="color: rgba(255, 255, 255, 0.6);">
                    Click the link in that email to verify your account, then sign in again.
                    Don't see it? Check your spam or promotions folder.
                </p>
                <div class="d-grid gap-2">
                    <button class="btn btn-primary" id="resend-verification-btn">
                        <i class="fas fa-paper-plane me-2"></i>Resend Verification Email
                    </button>
                    <button class="btn btn-outline-light" id="back-to-signin-from-verify">
                        <i class="fas fa-arrow-left me-2"></i>Back to Sign In
                    </button>
                </div>
            </div>
        `;
        
        // Seed the resend cooldown so users can't hammer it immediately.
        this._wireResendButton(userEmail, !!(vResult && (vResult.sent || vResult.success)));
        
        // Add event listener for back button
        document.getElementById('back-to-signin-from-verify')?.addEventListener('click', () => {
            this.showEmailSignInForm();
        });
    }
    
    // Wires the in-modal "Resend Verification Email" button to the backend with
    // a visible 60s cooldown. `alreadySent` starts the cooldown immediately.
    _wireResendButton(email, alreadySent) {
        const resendBtn = document.getElementById('resend-verification-btn');
        if (!resendBtn) return;

        let cooldownTimer = null;
        const startCooldown = (seconds) => {
            let remaining = seconds;
            resendBtn.disabled = true;
            const tick = () => {
                if (remaining <= 0) {
                    clearInterval(cooldownTimer);
                    resendBtn.disabled = false;
                    resendBtn.innerHTML = '<i class="fas fa-paper-plane me-2"></i>Resend Verification Email';
                    return;
                }
                resendBtn.innerHTML = `Resend available in ${remaining}s`;
                remaining -= 1;
            };
            tick();
            cooldownTimer = setInterval(tick, 1000);
        };

        if (alreadySent) startCooldown(60);

        resendBtn.addEventListener('click', async () => {
            if (resendBtn.disabled) return;
            resendBtn.disabled = true;
            resendBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Sending...';
            const data = await this.requestVerificationEmail({ email });
            if (data && data.alreadyVerified) {
                this.showSuccessMessage('Your email is already verified — you can sign in now.');
                startCooldown(60);
            } else if (data && data.cooldown) {
                startCooldown(data.retryAfter || 60);
            } else if (data && (data.sent || data.success)) {
                this.showSuccessMessage(`Verification email sent to ${email}. Check your inbox (and spam).`);
                startCooldown(60);
            } else {
                resendBtn.disabled = false;
                resendBtn.innerHTML = '<i class="fas fa-paper-plane me-2"></i>Resend Verification Email';
                this.showErrorMessage('Could not send verification email. Please try again shortly.');
            }
        });
    }
    
    async resendVerificationEmail(user) {
        const email = user?.email;
        const data = await this.requestVerificationEmail(user);
        if (data && data.alreadyVerified) {
            this.showSuccessMessage('Your email is already verified — you can sign in now.');
            return;
        }
        if (data && data.cooldown) {
            this.showErrorMessage(`Please wait ${data.retryAfter || 60}s before requesting another email.`);
            return;
        }
        if (data && (data.sent || data.success)) {
            this.showSuccessMessage(`Verification email sent to ${email}. Please check your inbox (and spam).`);
            const resendBtn = document.getElementById('resend-verification-btn');
            if (resendBtn) {
                resendBtn.innerHTML = '<i class="fas fa-check me-2"></i>Email Sent!';
                resendBtn.disabled = true;
                resendBtn.classList.remove('btn-primary');
                resendBtn.classList.add('btn-success');
                setTimeout(() => {
                    resendBtn.innerHTML = '<i class="fas fa-paper-plane me-2"></i>Resend Verification Email';
                    resendBtn.disabled = false;
                    resendBtn.classList.remove('btn-success');
                    resendBtn.classList.add('btn-primary');
                }, 60000);
            }
            return;
        }
        this.showErrorMessage('Failed to send verification email. Please try again later.');
    }
    
    showVerificationRequiredMessage(email) {
        const alertDiv = document.createElement('div');
        alertDiv.className = 'alert alert-warning alert-dismissible fade show position-fixed';
        alertDiv.style.cssText = 'top: 20px; left: 50%; transform: translateX(-50%); z-index: 9999; max-width: 500px; text-align: center;';
        
        alertDiv.innerHTML = `
            <h5><i class="fas fa-envelope me-2"></i>Verify Your Email</h5>
            <p class="mb-2">We've sent a verification email to <strong>${email}</strong></p>
            <p class="mb-0"><small>Please check your inbox (and spam folder) and click the verification link to activate your account.</small></p>
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;
        
        document.body.appendChild(alertDiv);
        
        setTimeout(() => {
            alertDiv.remove();
        }, 15000);
    }

    async signUpWithEmail() {
        const email = document.getElementById('signup-email').value;
        const password = document.getElementById('signup-password').value;
        const confirmPassword = document.getElementById('signup-confirm-password').value;

        if (!email || !password || !confirmPassword) {
            this.showErrorMessage('Please fill in all fields.');
            return;
        }

        if (password !== confirmPassword) {
            this.showErrorMessage('Passwords do not match.');
            return;
        }

        if (password.length < 6) {
            this.showErrorMessage('Password must be at least 6 characters long.');
            return;
        }

        try {
            // Set global flags BEFORE creating account to prevent race condition
            // Other auth listeners will check these flags and skip sign-out behavior
            window.emailSignUpInProgress = true;
            window.justSignedUp = true;
            // Store in sessionStorage so it persists across page reloads until tab is closed
            sessionStorage.setItem('justSignedUp', 'true');
            sessionStorage.setItem('unverifiedEmail', email);
            console.log('[AuthController] Set emailSignUpInProgress and justSignedUp flags (including sessionStorage)');
            
            const result = await createUserWithEmailAndPassword(auth, email, password);
            console.log('[AuthController] Account created successfully for:', result.user.email);

            // X (Twitter) Lead — fresh account creation (once per uid; see the
            // shared tracking include for the guard + conversion_id dedupe).
            try { window.llFireLead && window.llFireLead(result.user.uid, 'email_authcontroller'); } catch (e) {}
            
            // Send a branded verification email via SendGrid. The backend
            // generates the Firebase action link, so we no longer use Firebase's
            // unbranded default sender (which landed in spam).
            try {
                console.log('[AuthController] Requesting branded verification email...');
                const vResult = await this.requestVerificationEmail(result.user);
                console.log('[AuthController] Verification email request result:', vResult);

                // Keep user signed in - don't sign out
                this.closeAuthModal();

                // Clean popup: "just sent to <email>" + spam hint + working Resend.
                if (typeof window.showVerificationPopup === 'function') {
                    window.showVerificationPopup(result.user.email, {
                        justSent: !!(vResult && (vResult.sent || vResult.success))
                    });
                }
            } catch (emailError) {
                console.error('[AuthController] Failed to request verification email:', emailError);
                // Keep user signed in even if email failed
                this.closeAuthModal();
                this.showErrorMessage(`Account created but we couldn't send a verification email. Please try the Resend option in a moment.`);
            } finally {
                // Clear the emailSignUpInProgress flag immediately
                window.emailSignUpInProgress = false;
                console.log('[AuthController] Cleared emailSignUpInProgress flag');
                
                // Keep window.justSignedUp flag active for 10 seconds (for immediate checks)
                // sessionStorage flag persists until tab is closed or user verifies
                setTimeout(() => {
                    window.justSignedUp = false;
                    console.log('[AuthController] Cleared window.justSignedUp flag after delay');
                }, 10000);
            }
        } catch (error) {
            // Clear flags on error too
            window.emailSignUpInProgress = false;
            window.justSignedUp = false;
            sessionStorage.removeItem('justSignedUp');
            sessionStorage.removeItem('unverifiedEmail');
            console.error('Email sign-up error:', error);
            this.showErrorMessage(this.getErrorMessage(error.code));
        }
    }

    async signOut() {
        try {
            // Clear profile data before signing out
            window.currentUserProfile = null;
            if (typeof window.clearProfileForm === 'function') {
                window.clearProfileForm();
            }
            
            // Clear signup flags
            window.justSignedUp = false;
            sessionStorage.removeItem('justSignedUp');
            sessionStorage.removeItem('unverifiedEmail');
            
            // Clear the Flask session on the server
            try {
                await fetch("/api/logout", {
                    method: "POST",
                    credentials: 'include'
                });
                console.log('[AuthController] Server session cleared');
            } catch (logoutError) {
                console.warn('[AuthController] Could not clear server session:', logoutError);
            }
            
            await signOut(auth);
            this.showSuccessMessage('Signed out successfully!');
            console.log('[AuthController] User signed out, profile data cleared');
        } catch (error) {
            console.error('Sign-out error:', error);
            this.showErrorMessage('Failed to sign out. Please try again.');
        }
    }

    showEmailSignInForm() {
        const signinForm = document.getElementById('signin-form');
        const emailForm = document.getElementById('email-form');
        if (signinForm) signinForm.style.display = 'none';
        if (emailForm) {
            emailForm.style.display = 'block';
            const usernameInput = document.getElementById('username-input');
            if (usernameInput) usernameInput.style.display = 'none';
        }
    }

    showEmailSignUpForm() {
        const signinForm = document.getElementById('signin-form');
        const emailForm = document.getElementById('email-form');
        if (signinForm) signinForm.style.display = 'none';
        if (emailForm) {
            emailForm.style.display = 'block';
            const usernameInput = document.getElementById('username-input');
            if (usernameInput) usernameInput.style.display = 'block';
        }
    }

    updateUI() {
        const userDisplay = document.getElementById('user-display');
        const authButtons = document.getElementById('auth-buttons');
        
        // Always show profile button regardless of sign-in status
        if (userDisplay) {
            userDisplay.innerHTML = `
                <button class="btn profile-button" onclick="window.AuthController.showAuthModal()"
                    style="
                        background: rgba(255,255,255,0.05);
                        color: #fff;
                        border: 1px solid rgba(255,255,255,0.15);
                        padding: 10px 18px;
                        border-radius: 14px;
                        font-weight: 600;
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        box-shadow: 0 0 12px rgba(255,255,255,0.08);
                        transition: all 0.3s ease;
                    "
                    onmouseover="this.style.background='rgba(255,255,255,0.1)'"
                    onmouseout="this.style.background='rgba(255,255,255,0.05)'">
                    <i class="fas fa-user-circle"></i> Profile
                </button>
            `;
            userDisplay.style.display = 'block';
        }
        
        // Always hide the original auth buttons
        if (authButtons) {
            authButtons.style.display = 'none';
        }
    }

    showAuthModal() {
        console.log("AuthController: showAuthModal() called");
        const modal = document.getElementById('auth-modal');
        
        if (!modal) {
            console.error("Auth modal element not found!");
            return;
        }
        
        // First, ensure modal is in clean state
        modal.classList.remove('show');
        modal.style.backgroundColor = '';
        modal.style.backdropFilter = '';
        modal.style.webkitBackdropFilter = '';
        
        const modalContent = modal.querySelector('.auth-modal-content');
        if (modalContent) {
            // Remove animation class first (in case it's still there from previous open)
            modalContent.classList.remove('animate-in');
            
            // Clear any inline styles that might interfere
            modalContent.style.opacity = '';
            modalContent.style.transform = '';
            modalContent.style.animation = '';
            
            // Force reflow to reset animation state
            void modalContent.offsetHeight;
            
            // Add animation class to trigger entrance animation
            modalContent.classList.add('animate-in');
        }
        
        // Check if user is signed in and show appropriate content
        if (window.currentSignedInUser) {
            // User is signed in - show profile
            const user = window.currentSignedInUser;
            const userProfile = window.currentUserProfile;
            
            document.getElementById("signin-form").style.display = "none";
            document.getElementById("email-form").style.display = "none";
            document.getElementById("auth-loading").style.display = "none";
            document.getElementById("auth-error").style.display = "none";
            document.getElementById("auth-user-profile").style.display = "block";
            
            let displayName = user.email;
            if (userProfile && userProfile.username) displayName = userProfile.username;
            else if (user.displayName) displayName = user.displayName;
            
            const authUserInfo = document.getElementById("auth-user-info");
            if (authUserInfo) {
                authUserInfo.innerHTML = `
                    <img src="${user.photoURL || "/static/images/default-avatar.svg"}"
                         alt="Profile" class="user-avatar mb-3"
                         style="width: 80px; height: 80px; border-radius: 50%; margin: 0 auto; display: block;">
                    <h5 style="margin-bottom: 8px; color: #ffffff;">${displayName}</h5>
                `;
            }
            
            // Load profile form data (username, gender, age) after showing the profile
            if (typeof window.loadUserProfileForm === 'function') {
                setTimeout(() => {
                    window.loadUserProfileForm();
                }, 100);
            }
        } else {
            // User is signed out - show sign in options
            const signinForm = document.getElementById('signin-form');
            if (signinForm) {
                signinForm.style.display = 'block';
            }
            const emailForm = document.getElementById("email-form");
            const authLoading = document.getElementById("auth-loading");
            const authError = document.getElementById("auth-error");
            const authUserProfile = document.getElementById("auth-user-profile");
            
            if (emailForm) emailForm.style.display = "none";
            if (authLoading) authLoading.style.display = "none";
            if (authError) authError.style.display = "none";
            if (authUserProfile) authUserProfile.style.display = "none";
        }
        
        // Show the modal with proper display style
        modal.style.display = 'flex';
        
        // Add the 'show' class after a small delay to trigger CSS animation
        setTimeout(() => {
            modal.classList.add('show');
        }, 10);
        
        console.log("Auth modal displayed with flex");
    }

    closeAuthModal() {
        // Cancel any in-flight or queued social sign-in so it doesn't fire after the modal closes
        this._cancelPendingSocial();
        const modal = document.getElementById('auth-modal');
        if (modal) {
            // Remove all display and class states
            modal.style.display = 'none';
            modal.classList.remove('show');
            
            // Clear any lingering backdrop effects
            modal.style.backgroundColor = '';
            modal.style.backdropFilter = '';
            modal.style.webkitBackdropFilter = '';
            
            // Reset modal content - remove animation class for clean state
            const modalContent = modal.querySelector('.auth-modal-content');
            if (modalContent) {
                modalContent.classList.remove('animate-in');
                modalContent.style.opacity = '';
                modalContent.style.transform = '';
                modalContent.style.animation = '';
            }
            
            // Clear any body classes that might affect scrolling or overlays
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            
            // Reset all auth forms to initial state
            const signinForm = document.getElementById('signin-form');
            const emailForm = document.getElementById('email-form');
            const authLoading = document.getElementById('auth-loading');
            const authError = document.getElementById('auth-error');
            const authUserProfile = document.getElementById('auth-user-profile');
            
            if (signinForm) signinForm.style.display = 'block';
            if (emailForm) emailForm.style.display = 'none';
            if (authLoading) authLoading.style.display = 'none';
            if (authError) authError.style.display = 'none';
            if (authUserProfile) authUserProfile.style.display = 'none';
        }
    }

    showErrorMessage(message) {
        const alertDiv = document.createElement('div');
        alertDiv.className = 'alert alert-danger alert-dismissible fade show position-fixed';
        alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; max-width: 400px;';
        
        // Create message text node to prevent XSS
        const messageSpan = document.createElement('span');
        messageSpan.textContent = message;
        alertDiv.appendChild(messageSpan);
        
        // Create close button safely
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'btn-close';
        closeButton.setAttribute('data-bs-dismiss', 'alert');
        alertDiv.appendChild(closeButton);
        
        document.body.appendChild(alertDiv);
        
        setTimeout(() => {
            if (alertDiv.parentNode) {
                alertDiv.parentNode.removeChild(alertDiv);
            }
        }, 5000);
    }

    showSuccessMessage(message) {
        const alertDiv = document.createElement('div');
        alertDiv.className = 'alert alert-success alert-dismissible fade show position-fixed';
        alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; max-width: 400px;';
        
        // Create message text node to prevent XSS
        const messageSpan = document.createElement('span');
        messageSpan.textContent = message;
        alertDiv.appendChild(messageSpan);
        
        // Create close button safely
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'btn-close';
        closeButton.setAttribute('data-bs-dismiss', 'alert');
        alertDiv.appendChild(closeButton);
        
        document.body.appendChild(alertDiv);
        
        setTimeout(() => {
            if (alertDiv.parentNode) {
                alertDiv.parentNode.removeChild(alertDiv);
            }
        }, 5000);
    }

    getErrorMessage(errorCode) {
        switch (errorCode) {
            case 'auth/user-not-found':
                return 'No account found with this email address.';
            case 'auth/wrong-password':
                return 'Incorrect password.';
            case 'auth/invalid-credential':
                return 'Invalid email or password. Please check your credentials and try again.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists. Please sign in instead or use a different email.';
            case 'auth/weak-password':
                return 'Password is too weak. Please choose a stronger password (at least 6 characters).';
            case 'auth/invalid-email':
                return 'Invalid email address format. Please enter a valid email.';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later.';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection and try again.';
            case 'auth/user-disabled':
                return 'This account has been disabled. Please contact support.';
            case 'auth/operation-not-allowed':
                return 'This sign-in method is not allowed. Please contact support.';
            case 'auth/account-exists-with-different-credential':
                return 'An account already exists with this email using a different sign-in method.';
            default:
                return `Authentication error: ${errorCode}. Please try again or contact support.`;
        }
    }

    // Method to add auth state listeners
    addAuthStateListener(listener) {
        this.authStateListeners.push(listener);
    }

    // Method to remove auth state listeners
    removeAuthStateListener(listener) {
        const index = this.authStateListeners.indexOf(listener);
        if (index > -1) {
            this.authStateListeners.splice(index, 1);
        }
    }

    // Getter for current user
    getCurrentUser() {
        return this.currentUser;
    }

    // Check if user is authenticated
    isAuthenticated() {
        return this.currentUser !== null;
    }

    // Show forgot password form
    showForgotPasswordForm() {
        if (typeof window.showForgotPasswordForm === 'function') {
            window.showForgotPasswordForm();
        } else {
            const signinForm = document.getElementById('signin-form');
            const emailForm = document.getElementById('email-form');
            if (signinForm) signinForm.style.display = 'none';
            if (emailForm) emailForm.style.display = 'none';
            console.warn('[AuthController] Forgot password form handler not found');
        }
    }

    // Send password reset email
    async sendPasswordReset() {
        const email = document.getElementById('reset-email').value;

        if (!email) {
            this.showErrorMessage('Please enter your email address.');
            return;
        }

        try {
            await sendPasswordResetEmail(auth, email);
            this.showSuccessMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
            
            // Show confirmation message and option to go back to sign in
            const authContent = document.getElementById('auth-modal-content');
            authContent.innerHTML = `
                <div class="auth-form text-center">
                    <div class="mb-4">
                        <i class="fas fa-envelope-open-text" style="font-size: 3rem; color: #28a745;"></i>
                    </div>
                    <h3 class="mb-4">Email Sent!</h3>
                    <p class="mb-4" style="color: rgba(255, 255, 255, 0.8);">
                        We've sent a password reset link to:<br>
                        <strong>${email}</strong>
                    </p>
                    <p class="mb-4" style="color: rgba(255, 255, 255, 0.6);">
                        Check your inbox and click the link to reset your password. 
                        If you don't see the email, check your spam folder.
                    </p>
                    <button class="btn btn-primary w-100 mb-2" id="back-to-signin-btn">
                        Back to Sign In
                    </button>
                    <button class="btn btn-outline-secondary w-100" id="forgot-password-btn">
                        Resend Email
                    </button>
                </div>
            `;
        } catch (error) {
            console.error('Password reset error:', error);
            this.showErrorMessage(this.getPasswordResetErrorMessage(error.code));
        }
    }

    // Get password reset specific error messages
    getPasswordResetErrorMessage(errorCode) {
        switch (errorCode) {
            case 'auth/user-not-found':
                return 'No account found with this email address. Please check your email or create a new account.';
            case 'auth/invalid-email':
                return 'Invalid email address format. Please enter a valid email.';
            case 'auth/too-many-requests':
                return 'Too many reset requests. Please wait a few minutes before trying again.';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection and try again.';
            default:
                return `Password reset error: ${errorCode}. Please try again or contact support.`;
        }
    }
}